import { useEffect, useState } from "react";
import { Link, useRouteLoaderData, useSubmit } from "react-router-dom";
import { saveAs } from "file-saver";

import UserInfo from "src/components/common/userInfo";
import { dateDurationCalc, getFileNameFromUrl, myAxios, testerEmails } from "src/components/common/functions";
import { useAuthStore, User } from "src/components/main/authStore";
import { useUiStore } from "src/components/main/uiStore";
import { PerDiem } from "./types";

export default function PerDiemDetail() {
    const { perDiem } = useRouteLoaderData("PerDiem") as { perDiem: PerDiem };
    const user = useAuthStore(state => state.user) as User;
    const setShowLoader = useUiStore(state => state.setShowLoader);
    const submit = useSubmit();
    const [comments, setComments] = useState(perDiem.approverNotes ?? "");
    const [curPdfId, setCurPdfId] = useState(perDiem.pdfId ?? "");

    useEffect(() => {
        setComments(perDiem.approverNotes ?? "");
        setCurPdfId(perDiem.pdfId ?? "");
    }, [perDiem]);

    const canEdit = (
        user.perDiem === "editAll" ||
        (user.perDiem === "edit" && [perDiem.requesterId, perDiem.creatorId].includes(user.id))
    ) && !["Approved", "Rejected"].includes(perDiem.status) && perDiem.void === "no";
    const canApprove = perDiem.approver?.value === user.id && perDiem.void === "no" && !["Approved", "Rejected"].includes(perDiem.status);

    const submitSimple = (action: string, values: Record<string, string>) => {
        const formData = new FormData();
        Object.entries(values).forEach(([key, value]) => formData.append(key, value));
        submit(formData, { method: "post", action });
    };

    const handleDownload = async (pdfId: string) => {
        try {
            const response = await fetch(pdfId);
            if (!response.ok) throw new Error("Failed to fetch file");
            saveAs(await response.blob(), getFileNameFromUrl(pdfId)!);
        } catch (error) {
            window.alert("Error downloading file:" + error);
        }
    };
    const handleRemovePdf = async () => {
        if (!curPdfId) return;
        if (!window.confirm("Are you sure to remove this per diem file?")) return;
        const formData = new FormData();
        formData.append("id", String(perDiem.id));
        setShowLoader(true);
        const res = await myAxios({ fileName: "perDiem/deletePerDiemPDF.php", formData });
        setShowLoader(false);
        if (!res) return;
        setCurPdfId("");
    };

    return (
        <div className = "border" style = {{ borderRadius: "6px" }}>
            <div style = {{ display: "flex" }}>
                <table className = "table table-responsive-xl noTopBorder">
                    <tbody>
                        <tr>
                            <th>Status</th>
                            <td><span className = "badge text-bg-primary ms-2">{perDiem.status}</span></td>
                        </tr>
                        <tr>
                            <th>Requester</th>
                            <td><UserInfo name = {perDiem.requester.label} id = {perDiem.requester.value} /></td>
                        </tr>
                        <tr>
                            <th>Department</th>
                            <td><span className = "ms-2">{perDiem.requester.department}</span></td>
                        </tr>
                        <tr>
                            <th>Project</th>
                            <td><Link to = {`/Projects/${perDiem.projectId}`}>{perDiem.projectName}</Link></td>
                        </tr>
                        <tr>
                            <th>Dates</th>
                            <td>{perDiem.startDate} - {perDiem.endDate}</td>
                        </tr>
                        <tr>
                            <th>Duration</th>
                            <td>{dateDurationCalc(perDiem.startDate, perDiem.endDate)}</td>
                        </tr>
                        <tr>
                            <th>Hotel Name</th>
                            <td>{perDiem.hotelName}</td>
                        </tr>
                        <tr>
                            <th>Hotel Address</th>
                            <td>{perDiem.hotelAddress}</td>
                        </tr>
                        <tr>
                            <th>Notes</th>
                            <td><span className = "ms-2" style = {{ whiteSpace: "pre-wrap" }}>{perDiem.notes}</span></td>
                        </tr>
                        <tr>
                            <th>Paid</th>
                            <td>{perDiem.paid}</td>
                        </tr>
                        {perDiem.notifiedAt ? (
                            <tr>
                                <th>Notified By</th>
                                <td>
                                    <UserInfo name = {perDiem.notifierName} id = {perDiem.notifiedBy} />
                                    <span className = "ms-2">{perDiem.notifiedAt}</span>
                                </td>
                            </tr>
                        ) : null}
                        <tr>
                            <th>Approver</th>
                            <td><UserInfo name = {perDiem.approverName} id = {perDiem.approverId} /></td>
                        </tr>
                        <tr>
                            <th>Review Time</th>
                            <td>{perDiem.approvalTime}</td>
                        </tr>
                        <tr>
                            <th>Approver Comments</th>
                            <td><span className = "ms-2" style = {{ whiteSpace: "pre-wrap" }}>{perDiem.approverNotes}</span></td>
                        </tr>
                        <tr>
                            <th>Creator</th>
                            <td>
                                <UserInfo name = {perDiem.creatorName} id = {perDiem.creatorId} />
                                <span className = "ms-2">{perDiem.createdAt}</span>
                            </td>
                        </tr>
                        {perDiem.updaterId ? (
                            <tr>
                                <th>Updater</th>
                                <td>
                                    <UserInfo name = {perDiem.updaterName} id = {perDiem.updaterId} />
                                    <span className = "ms-2">{perDiem.updatedAt}</span>
                                </td>
                            </tr>
                        ) : null}
                        {perDiem.void === "yes" ? (
                            <>
                                <tr>
                                    <th className = "text-danger">Void Reason</th>
                                    <td>{perDiem.voidReason}</td>
                                </tr>
                                <tr>
                                    <th>Validate Reason</th>
                                    <td>{perDiem.validateReason}</td>
                                </tr>
                            </>
                        ) : null}
                    </tbody>
                </table>
            </div>
            <div className = "mb-3">
                {canEdit ? <Link className = "btn btn-outline-primary" to = "Edit">Edit</Link> : null}
                {perDiem.void === "no" ? (
                    <button
                        type = "button"
                        className = "btn btn-danger ms-2"
                        onClick = {() => {
                            if (!window.confirm("Are you sure to void this per diem form?")) return;
                            let voidReason = window.prompt("reason to void:");
                            if (voidReason === null) return;
                            while (voidReason === "") {
                                window.alert("Reason is required to void.");
                                voidReason = window.prompt("reason to void:");
                                if (voidReason === null) return;
                            }
                            submitSimple("Void", { id: String(perDiem.id), voidReason });
                        }}
                    >
                        Void
                    </button>
                ) : (
                    <>
                        {testerEmails.includes(user.email) ? (
                            <button
                                type = "button"
                                className = "btn btn-primary ms-2"
                                onClick = {() => {
                                    if (!window.confirm("Are you sure to validate this per diem form?")) return;
                                    let validateReason = window.prompt("reason to validate:");
                                    if (validateReason === null) return;
                                    while (validateReason === "") {
                                        window.alert("Reason is required to validate.");
                                        validateReason = window.prompt("reason to validate:");
                                        if (validateReason === null) return;
                                    }
                                    submitSimple("Validate", { id: String(perDiem.id), validateReason });
                                }}
                            >
                                Validate
                            </button>
                        ) : null}
                        <button
                            type = "button"
                            className = "btn btn-danger ms-2"
                            onClick = {() => {
                                if (!window.confirm("Are you sure to delete this per diem form?\n\nThis action cannot be undone.")) return;
                                submitSimple("Delete", { id: String(perDiem.id) });
                            }}
                        >
                            Delete
                        </button>
                    </>
                )}
                {perDiem.void === "no" && perDiem.approverId !== user.id && !["Approved", "Rejected"].includes(perDiem.status) ? (
                    <button
                        type = "button"
                        className = "btn btn-info ms-2"
                        onClick = {() => {
                            if (!window.confirm("Are you sure to notify the approver?")) return;
                            submitSimple("Notify", { id: String(perDiem.id) });
                        }}
                    >
                        Notify
                    </button>
                ) : null}
                {curPdfId ? (
                    <>
                        <button
                            type = "button"
                            className = "btn btn-primary ms-2"
                            onClick = {() => handleDownload(curPdfId)}
                        >
                            PDF
                        </button>
                        <button
                            type = "button"
                            className = "btn btn-danger ms-2 text-white"
                            onClick = {() => void handleRemovePdf()}
                        >
                            &times;
                        </button>
                    </>
                ) : null}
                <Link className = "btn btn-outline-secondary float-end" to = ".." relative = "path">Back</Link>
            </div>
            {canApprove ? (
                <>
                    <div className = "mx-3">
                        <label className = "fw-bold mb-0" htmlFor = "approverNotes">Approver Comments:</label>
                        <textarea
                            id = "approverNotes"
                            className = "form-control"
                            rows = {4}
                            value = {comments}
                            onChange = {e => setComments(e.currentTarget.value)}
                            name = "comments"
                        />
                    </div>
                    <div>
                        {[
                            { title: "Approve", decision: "Approved", word: "approve", color: "btn-success" },
                            { title: "Reject", decision: "Rejected", word: "reject", color: "btn-danger" },
                        ].map(item => (
                            <button
                                key = {item.decision}
                                type = "button"
                                className = {`btn ${item.color} m-3`}
                                onClick = {() => {
                                    if (item.word === "reject" && !comments) {
                                        window.alert("Comments are required for rejection.");
                                        return;
                                    }
                                    if (!window.confirm(`Are you sure to ${item.word} this per diem request?`)) return;
                                    submitSimple("Approve", {
                                        id: String(perDiem.id),
                                        approverDecision: item.decision,
                                        approverNotes: comments,
                                    });
                                }}
                            >
                                {item.title}
                            </button>
                        ))}
                    </div>
                </>
            ) : null}
        </div>
    );
}
